import { Trans, useTranslation } from "react-i18next";
import { IconX } from "@tabler/icons-react";
import ShareV2Icon from "../../assets/icons/share-v2.svg?react";
import { cn } from "../../lib/utils";
import Skeleton from "../skeleton/Skeleton";
import { AccessBadge, PlayingBadge } from "../ui/PublicationCard";
import { Tag, type TagVariant } from "../ui/Tag";

/**
 * One article of a desktop feed (Figma "ArticleRow" and the feed's "hero").
 *
 * `row` is the compact line of the list, with a thumbnail on the right. `hero`
 * opens the feed: a larger title, the summary and the authors, the figure
 * beside them. Every layout always shows its save and share actions, on the
 * row's end edge (in `row` and `collection`, under the thumbnail): an action
 * shown on hover only is one a reader never finds. `collection` is the row of a collection's list (Figma
 * "D3.explore.collection"): the authors under the title, the reading time in
 * the meta line, and a lock on open access papers only.
 */
type DesktopArticleRowProps = {
  layout: "row" | "hero" | "collection";
  title: string;
  /**
   * The line above the title: the journal a paper is "In", with its impact
   * factor (`null` when none is on record), or the partner a room resource
   * comes from, on its own. `null` leaves the line out.
   */
  source:
    | {
        kind: "journal";
        name: string;
        impactFactor: number | null;
        onClick: (() => void) | null;
      }
    | { kind: "partner"; label: string }
    | null;
  /**
   * Shown by the hero and the collection row, under the title: the authors of
   * a paper, or the subject of a room resource.
   */
  author: string;
  /** Shown by the hero only. */
  description: string | null;
  date: string;
  /** `null` where the list does not say how long a paper takes to read. */
  readingMinutes: number | null;
  meta: string;
  metaVariant: TagVariant;
  access: "open" | "closed";
  imageUrl: string | null;
  /** The audio player has this article loaded, playing or paused. */
  isInPlayer: boolean;
  /** The save control (`DesktopSaveButton`), or null where saving is not offered. */
  bookmark: React.JSX.Element | null;
  onShare: (() => void) | null;
  /** Takes the paper out of the list it is in (the owner's collection). */
  onRemove: (() => void) | null;
  onClick: () => void;
};

function SourceLine({
  layout,
  source,
}: {
  layout: DesktopArticleRowProps["layout"];
  source: NonNullable<DesktopArticleRowProps["source"]>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const sizeClass = layout === "hero" ? "text-ui-body" : "text-footnote";
  switch (source.kind) {
    case "partner":
      return (
        <span
          className={cn(
            "min-w-0 truncate font-semibold text-ds-primary",
            sizeClass
          )}
        >
          {source.label}
        </span>
      );
    case "journal": {
      const { onClick } = source;
      return (
        <div className={cn("flex min-w-0 items-center gap-1", sizeClass)}>
          <span className="flex min-w-0 items-center gap-1 text-ds-slate-600">
            <Trans
              i18nKey="In <name>{{name}}</name>"
              values={{ name: source.name }}
              components={{
                name:
                  onClick === null ? (
                    <span className="min-w-0 truncate font-semibold text-ds-primary" />
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onClick();
                      }}
                      className="min-w-0 cursor-pointer truncate font-semibold text-ds-primary hover:underline"
                    />
                  ),
              }}
            />
          </span>
          {source.impactFactor === null ? null : (
            <Tag
              size="md"
              variant="slate"
              state="idle"
              icon={null}
              className="shrink-0"
            >
              {t("IF {{value}}", { value: source.impactFactor.toFixed(1) })}
            </Tag>
          )}
        </div>
      );
    }
    default:
      throw new Error(source satisfies never);
  }
}

function Actions({
  bookmark,
  onShare,
  onRemove,
}: {
  bookmark: React.JSX.Element | null;
  onShare: (() => void) | null;
  onRemove: (() => void) | null;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  if (bookmark === null && onShare === null && onRemove === null) return null;
  return (
    // The last button's 4px inset is given back, so its glyph ends on the
    // column's edge, where the title ends.
    <div className="-mr-1 ml-auto flex shrink-0 items-center gap-1">
      {onShare === null ? null : (
        <button
          type="button"
          aria-label={t("Share")}
          onClick={(e) => {
            e.stopPropagation();
            onShare();
          }}
          className="flex size-7 cursor-pointer items-center justify-center rounded-md text-ds-black hover:bg-ds-slate-100"
        >
          <ShareV2Icon className="size-5" />
        </button>
      )}
      {bookmark}
      {onRemove === null ? null : (
        <button
          type="button"
          aria-label={t("Remove from this collection")}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="flex size-7 cursor-pointer items-center justify-center rounded-md text-ds-black hover:bg-ds-slate-100"
        >
          <IconX className="size-5" stroke={1.5} />
        </button>
      )}
    </div>
  );
}

function DesktopArticleRow({
  layout,
  title,
  source,
  author,
  description,
  date,
  readingMinutes,
  meta,
  metaVariant,
  access,
  imageUrl,
  isInPlayer,
  bookmark,
  onShare,
  onRemove,
  onClick,
}: DesktopArticleRowProps): React.JSX.Element {
  const journalLine =
    source === null ? null : <SourceLine layout={layout} source={source} />;
  const { t } = useTranslation();
  // A collection row locks nothing: only an open paper carries the badge.
  const showsAccess = layout !== "collection" || access === "open";
  // An undated paper leaves its date out rather than doubling a separator.
  const metaText = [
    date,
    ...(readingMinutes === null
      ? []
      : [t("{{count}} min read", { count: readingMinutes })]),
  ]
    .filter((part) => part !== "")
    .join(" · ");
  const metaLine = (
    <div className="flex min-w-0 flex-wrap items-center gap-1 text-caption text-ds-slate-600">
      <Tag
        size="md"
        variant={metaVariant}
        state="idle"
        icon={null}
        className="shrink-0"
      >
        {meta}
      </Tag>
      {metaText === "" ? (
        showsAccess ? (
          <span>·</span>
        ) : null
      ) : (
        <span className="whitespace-nowrap">
          · {metaText}
          {showsAccess ? " ·" : null}
        </span>
      )}
      {showsAccess ? <AccessBadge access={access} /> : null}
      {isInPlayer ? <PlayingBadge /> : null}
    </div>
  );

  // A clickable row holds buttons (journal, save, share), so it cannot be a
  // <button> itself.
  const rowProps = {
    role: "button",
    tabIndex: 0,
    onClick,
    onKeyDown: (e: React.KeyboardEvent) => {
      // A key pressed on a child button (journal, save, share) is that
      // button's, not the row's.
      if (e.target !== e.currentTarget) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    },
  };

  switch (layout) {
    case "hero":
      return (
        <div
          {...rowProps}
          className="flex w-full cursor-pointer flex-col gap-3 pb-6 text-left font-dm-sans"
        >
          <div className="flex flex-col gap-2">
            {journalLine}
            <p className="m-0 text-heading-lg leading-8.5 font-semibold text-ds-black">
              {title}
            </p>
          </div>
          <div className="flex items-stretch gap-3">
            <div className="flex min-w-0 flex-1 flex-col gap-3">
              {description === null ? null : (
                <p className="m-0 line-clamp-5 text-ui-body text-ds-slate-700">
                  {description}
                </p>
              )}
              <div className="mt-auto flex flex-col gap-1">
                {author === "" ? null : (
                  <span className="text-footnote text-ds-slate-600">
                    {author}
                  </span>
                )}
                <div className="flex items-center justify-between gap-2">
                  {metaLine}
                  <Actions
                    bookmark={bookmark}
                    onShare={onShare}
                    onRemove={onRemove}
                  />
                </div>
              </div>
            </div>
            {imageUrl === null ? null : (
              <img
                src={imageUrl}
                alt=""
                className="h-42 w-56 shrink-0 rounded-xl bg-ds-slate-100 object-cover"
              />
            )}
          </div>
        </div>
      );
    case "row":
    case "collection":
      return (
        <div
          {...rowProps}
          className="group flex w-full cursor-pointer items-start gap-4 py-4 text-left font-dm-sans"
        >
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            {journalLine}
            <p className="m-0 text-heading-xs font-semibold text-ds-black group-hover:text-ds-primary">
              {title}
            </p>
            {layout === "collection" && author !== "" ? (
              <span className="text-footnote text-ds-slate-600">{author}</span>
            ) : null}
            {metaLine}
          </div>
          {/* The thumbnail's column, as tall as the text: the actions sit at
              its foot, on the row's end edge, level with the meta line. */}
          <div className="flex shrink-0 flex-col items-end gap-2 self-stretch">
            {imageUrl === null ? null : (
              <img
                src={imageUrl}
                alt=""
                className="h-22.5 w-30 rounded-lg bg-ds-slate-100 object-cover"
              />
            )}
            <div className="mt-auto">
              <Actions
                bookmark={bookmark}
                onShare={onShare}
                onRemove={onRemove}
              />
            </div>
          </div>
        </div>
      );
    default:
      throw new Error(layout satisfies never);
  }
}

/** The loading silhouette of a `row`, same height as the real one. */
function DesktopArticleRowSkeleton(): React.JSX.Element {
  return (
    <div className="flex w-full items-start gap-4 py-4">
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <Skeleton className="rounded" width="40%" height={16} />
        <Skeleton className="rounded" width="90%" height={20} />
        <Skeleton className="rounded" width="60%" height={20} />
        <Skeleton className="rounded" width="35%" height={16} />
      </div>
      <Skeleton className="shrink-0 rounded-lg" width={120} height={90} />
    </div>
  );
}

/**
 * The loading silhouette of a `hero`: the journal line, a title on two lines,
 * the summary beside the picture, and the meta line at its foot. It draws the
 * hero the feed is about to show, so nothing jumps when it lands.
 */
function DesktopArticleHeroSkeleton(): React.JSX.Element {
  return (
    <div className="flex w-full flex-col gap-3 pb-6">
      <div className="flex flex-col gap-2">
        <Skeleton className="rounded" width="30%" height={20} />
        <Skeleton className="rounded" width="95%" height={30} />
        <Skeleton className="rounded" width="70%" height={30} />
      </div>
      <div className="flex items-stretch gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <Skeleton className="rounded" width="100%" height={16} />
          <Skeleton className="rounded" width="100%" height={16} />
          <Skeleton className="rounded" width="90%" height={16} />
          <Skeleton className="rounded" width="60%" height={16} />
          <div className="mt-auto">
            <Skeleton className="rounded" width="40%" height={20} />
          </div>
        </div>
        <Skeleton className="shrink-0 rounded-xl" width={224} height={168} />
      </div>
    </div>
  );
}

export {
  DesktopArticleHeroSkeleton,
  DesktopArticleRow,
  DesktopArticleRowSkeleton,
};
